/**
 * OfflineIndicator — Header badge showing offline/syncing status.
 *
 * States:
 *  - Online, nothing pending: renders nothing
 *  - Offline: red "OFFLINE" badge
 *  - Syncing: yellow "Syncing N items..." with spinner
 *  - Sync error: orange "Sync error" badge
 */
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import type { OfflineSyncState } from '@/hooks/useOfflineSync';

interface OfflineIndicatorProps {
  isOnline: boolean;
  syncState: OfflineSyncState;
}

export function OfflineIndicator({ isOnline, syncState }: OfflineIndicatorProps) {
  const { isSyncing, pendingCount, lastError, progress } = syncState;

  if (isOnline && !isSyncing && pendingCount === 0 && !lastError) {
    return null;
  }

  if (!isOnline) {
    return (
      <div className="flex items-center gap-1.5 rounded-md bg-red-600 px-2.5 py-1 text-xs font-semibold text-white">
        <WifiOff className="h-3.5 w-3.5" />
        OFFLINE
        {pendingCount > 0 && (
          <span className="ml-1 rounded bg-red-800 px-1 py-0.5 text-xs">
            {pendingCount} queued
          </span>
        )}
      </div>
    );
  }

  if (isSyncing) {
    const syncLabel = progress
      ? `Syncing ${progress.completed + 1}/${progress.total}`
      : `Syncing${pendingCount > 0 ? ` ${pendingCount} items` : ''}…`;

    return (
      <div className="flex items-center gap-1.5 rounded-md bg-yellow-500 px-2.5 py-1 text-xs font-semibold text-white">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
        {syncLabel}
      </div>
    );
  }

  if (lastError) {
    return (
      <div
        className="flex items-center gap-1.5 rounded-md bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white"
        title={lastError}
      >
        <Wifi className="h-3.5 w-3.5" />
        Sync error
      </div>
    );
  }

  // Online with pending items (queued but not yet syncing)
  if (pendingCount > 0) {
    return (
      <div className="flex items-center gap-1.5 rounded-md bg-blue-500 px-2.5 py-1 text-xs font-semibold text-white">
        <RefreshCw className="h-3.5 w-3.5" />
        {pendingCount} pending
      </div>
    );
  }

  return null;
}
